import React, { useState, useEffect } from 'react';
import { usersAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
    UserCog,
    MoreVertical,
    Key,
    MapPin,
    Phone,
    Mail,
    Loader2,
    X,
    Plus,
    Pencil,
    Trash2,
    Shield,
    ShieldOff,
    LockOpen,
    UserPlus,
    Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { apiErrorMessage } from '../lib/apiError';

const formatLastLogin = (iso) => {
    if (!iso) return 'mai';
    try {
        const d = new Date(iso);
        return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
        return 'mai';
    }
};

const UsersPage = () => {
    const { isAdmin, user: currentAuthUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingUser, setEditingUser] = useState(null);
    const [countriesInput, setCountriesInput] = useState('');
    const [editedCountries, setEditedCountries] = useState([]);
    const [editingProfile, setEditingProfile] = useState(null);
    const [profileForm, setProfileForm] = useState({ email: '', name: '', phone: '' });
    const [creating, setCreating] = useState(false);
    const [createForm, setCreateForm] = useState({ name: '', email: '', phone: '', role: 'commerciale', password: '' });
    const [deleting, setDeleting] = useState(null);
    const [reassignTo, setReassignTo] = useState('');
    const [managingUser, setManagingUser] = useState(null); // commerciale a cui assegnare un area manager
    const [selectedManager, setSelectedManager] = useState('');

    const roleLabel = (r) => ({ admin: 'Amministratore', area_manager: 'Area Manager', backoffice: 'Backoffice', addetto_consegna: 'Addetto alla Consegna' }[r] || 'Commerciale');
    const areaManagers = users.filter(u => u.role === 'area_manager');
    const userNameById = Object.fromEntries(users.map(u => [u.id, u.name]));

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        try {
            const response = await usersAPI.getAll();
            setUsers(response.data);
        } catch (error) {
            toast.error('Errore nel caricamento utenti');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenCountriesDialog = (user) => {
        setEditingUser(user);
        setEditedCountries([...user.countries]);
        setCountriesInput('');
    };

    const handleAddCountry = () => {
        if (countriesInput.trim() && !editedCountries.includes(countriesInput.trim())) {
            setEditedCountries([...editedCountries, countriesInput.trim()]);
            setCountriesInput('');
        }
    };

    const handleRemoveCountry = (country) => {
        setEditedCountries(editedCountries.filter(c => c !== country));
    };

    const handleSaveCountries = async () => {
        try {
            await usersAPI.updateCountries(editingUser.id, editedCountries);
            toast.success('Paesi aggiornati');
            setEditingUser(null);
            loadUsers();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore nel salvataggio'));
        }
    };

    const handleResetPassword = async (userId) => {
        if (!window.confirm('Reimpostare la password a "pezzaioli2024"? L\'utente dovrà cambiarla al prossimo accesso.')) return;
        
        try {
            await usersAPI.resetPassword(userId);
            toast.success('Password reimpostata');
        } catch (error) {
            toast.error('Errore nel reset password');
        }
    };

    const handleResetMfa = async (userId) => {
        if (!window.confirm('Reimpostare l\'MFA? L\'utente dovrà riconfigurare l\'app authenticator al prossimo accesso.')) return;
        try {
            await usersAPI.resetMfa(userId);
            toast.success('MFA reimpostata');
        } catch (error) {
            toast.error('Errore nel reset MFA');
        }
    };

    const handleUnlock = async (user) => {
        if (!window.confirm(`Sbloccare l'account di ${user.name}? Potrà nuovamente accedere.`)) return;
        try {
            await usersAPI.unlock(user.id);
            toast.success('Account sbloccato');
            loadUsers();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore nello sblocco'));
        }
    };

    const [forcePwdOpen, setForcePwdOpen] = useState(false);
    const [forcingPwd, setForcingPwd] = useState(false);
    const handleForcePasswordChangeAll = async () => {
        setForcingPwd(true);
        try {
            const res = await usersAPI.forcePasswordChangeAll();
            toast.success(res.data?.message || 'Fatto');
            setForcePwdOpen(false);
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore durante l\'operazione'));
        } finally {
            setForcingPwd(false);
        }
    };

    const [recoveryCodesDialog, setRecoveryCodesDialog] = useState(null); // {name, codes}

    const handleRegenerateRecoveryCodes = async (user) => {
        if (!window.confirm(`Rigenerare i codici di recupero per ${user.name}? I codici precedenti non saranno più validi.`)) return;
        try {
            const res = await usersAPI.regenerateRecoveryCodes(user.id);
            setRecoveryCodesDialog({ name: user.name, codes: res.data.recovery_codes });
        } catch (error) {
            toast.error(apiErrorMessage(error) || "Errore: l'utente deve prima configurare l'MFA");
        }
    };

    const handleOpenProfileDialog = (user) => {
        setEditingProfile(user);
        setProfileForm({
            email: user.email || '',
            name: user.name || '',
            phone: user.phone || ''
        });
    };

    const handleSaveProfile = async (e) => {
        e.preventDefault();
        try {
            const payload = {};
            if (profileForm.email && profileForm.email !== editingProfile.email) payload.email = profileForm.email;
            if (profileForm.name && profileForm.name !== editingProfile.name) payload.name = profileForm.name;
            if (profileForm.phone !== editingProfile.phone) payload.phone = profileForm.phone;
            if (Object.keys(payload).length === 0) {
                setEditingProfile(null);
                return;
            }
            await usersAPI.updateProfile(editingProfile.id, payload);
            toast.success('Profilo aggiornato');
            setEditingProfile(null);
            loadUsers();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore nel salvataggio'));
        }
    };

    const handleSetRole = async (user, newRole) => {
        if (user.role === newRole) return;
        if (!window.confirm(`Cambiare il ruolo di ${user.name} in ${roleLabel(newRole)}?`)) return;
        try {
            await usersAPI.updateRole(user.id, newRole);
            toast.success(`Ruolo aggiornato: ${roleLabel(newRole)}`);
            loadUsers();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore nel cambio ruolo'));
        }
    };

    const handleOpenManager = (user) => {
        setManagingUser(user);
        setSelectedManager(user.manager_id || 'none');
    };

    const handleSaveManager = async () => {
        try {
            await usersAPI.setManager(managingUser.id, selectedManager === 'none' ? null : selectedManager);
            toast.success('Assegnazione al team aggiornata');
            setManagingUser(null);
            setSelectedManager('');
            loadUsers();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore nell\'assegnazione'));
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                name: createForm.name.trim(),
                email: createForm.email.trim().toLowerCase(),
                role: createForm.role,
                phone: createForm.phone.trim(),
                countries: [],
            };
            if (createForm.password) payload.password = createForm.password;
            await usersAPI.create(payload);
            toast.success(`Utente creato. Password: ${createForm.password || 'pezzaioli2024'}`);
            setCreating(false);
            setCreateForm({ name: '', email: '', phone: '', role: 'commerciale', password: '' });
            loadUsers();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore nella creazione'));
        }
    };

    const handleConfirmDelete = async () => {
        if (!deleting) return;
        try {
            const res = await usersAPI.delete(deleting.id, reassignTo || undefined);
            const stats = res.data?.reassigned || {};
            const reassignedTotal = (stats.clients || 0) + (stats.opportunities || 0) + (stats.activities || 0);
            if (reassignedTotal > 0) {
                toast.success(`Utente eliminato. Riassegnati: ${stats.clients} clienti, ${stats.opportunities} opportunità, ${stats.activities} attività`);
            } else {
                toast.success('Utente eliminato');
            }
            setDeleting(null);
            setReassignTo('');
            loadUsers();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore nell\'eliminazione'));
        }
    };

    if (!isAdmin()) {
        return <Navigate to="/dashboard" replace />;
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="users-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl uppercase tracking-tight">Gestione Utenti</h1>
                    <p className="text-muted-foreground">
                        {users.length} utenti nel sistema
                    </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                        variant="outline"
                        className="font-heading"
                        onClick={() => setForcePwdOpen(true)}
                        data-testid="force-password-change-all-btn"
                    >
                        <Key size={18} className="mr-2" />
                        FORZA CAMBIO PASSWORD
                    </Button>
                    <Button
                        className="btn-primary font-heading"
                        onClick={() => setCreating(true)}
                        data-testid="add-user-btn"
                    >
                        <UserPlus size={18} className="mr-2" />
                        NUOVO UTENTE
                    </Button>
                </div>
            </div>

            <AlertDialog open={forcePwdOpen} onOpenChange={setForcePwdOpen}>
                <AlertDialogContent data-testid="force-password-change-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-heading uppercase">Forza cambio password</AlertDialogTitle>
                        <AlertDialogDescription>
                            Tutti gli utenti (tranne te) dovranno impostare una nuova password al prossimo accesso.
                            Le password attuali NON vengono modificate: gli utenti accedono normalmente e poi scelgono la nuova password.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={forcingPwd}>Annulla</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); handleForcePasswordChangeAll(); }}
                            disabled={forcingPwd}
                            data-testid="confirm-force-password-change-btn"
                        >
                            {forcingPwd ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                            Conferma
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Users Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {users.map((user) => (
                    <Card key={user.id} className="card-hover" data-testid={`user-card-${user.id}`}>
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between">
                                <div className="flex items-center gap-3">
                                    <div className={`w-12 h-12 rounded-sm flex items-center justify-center text-white font-heading text-lg ${
                                        user.role === 'admin' ? 'bg-primary' : 'bg-secondary'
                                    }`}>
                                        {user.name.charAt(0)}
                                    </div>
                                    <div>
                                        <h3 className="font-medium">{user.name}</h3>
                                        <div className="flex items-center gap-1 flex-wrap">
                                            <Badge variant={user.role === 'admin' ? 'default' : (user.role === 'area_manager' ? 'outline' : 'secondary')} className="text-xs" data-testid={`role-badge-${user.id}`}>
                                                {roleLabel(user.role)}
                                            </Badge>
                                            {user.role === 'commerciale' && user.manager_id && (
                                                <Badge variant="outline" className="text-xs" data-testid={`manager-badge-${user.id}`}>
                                                    Team: {userNameById[user.manager_id] || '—'}
                                                </Badge>
                                            )}
                                            {user.is_locked && (
                                                <Badge variant="destructive" className="text-xs" data-testid={`locked-badge-${user.id}`}>
                                                    Bloccato
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="icon" data-testid={`user-menu-${user.id}`}>
                                            <MoreVertical size={18} />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem onClick={() => handleOpenProfileDialog(user)} data-testid={`edit-profile-${user.id}`}>
                                            <Pencil size={16} className="mr-2" />
                                            Modifica Profilo
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleOpenCountriesDialog(user)}>
                                            <MapPin size={16} className="mr-2" />
                                            Modifica Paesi
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleSetRole(user, 'commerciale')} data-testid={`role-commerciale-${user.id}`}>
                                            <UserCog size={16} className="mr-2" />
                                            Rendi Commerciale
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleSetRole(user, 'area_manager')} data-testid={`role-area-manager-${user.id}`}>
                                            <Shield size={16} className="mr-2" />
                                            Rendi Area Manager
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleSetRole(user, 'backoffice')} data-testid={`role-backoffice-${user.id}`}>
                                            <UserCog size={16} className="mr-2" />
                                            Rendi Backoffice
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleSetRole(user, 'addetto_consegna')} data-testid={`role-addetto-${user.id}`}>
                                            <UserCog size={16} className="mr-2" />
                                            Rendi Addetto alla Consegna
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleSetRole(user, 'admin')} data-testid={`role-admin-${user.id}`}>
                                            <Shield size={16} className="mr-2" />
                                            Rendi Amministratore
                                        </DropdownMenuItem>
                                        {user.role === 'commerciale' && (
                                            <DropdownMenuItem onClick={() => handleOpenManager(user)} data-testid={`assign-manager-${user.id}`}>
                                                <UserPlus size={16} className="mr-2" />
                                                Assegna Area Manager
                                            </DropdownMenuItem>
                                        )}
                                        <DropdownMenuItem onClick={() => handleResetPassword(user.id)} data-testid={`reset-password-${user.id}`}>
                                            <Key size={16} className="mr-2" />
                                            Reset Password
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleResetMfa(user.id)} data-testid={`reset-mfa-${user.id}`}>
                                            <ShieldOff size={16} className="mr-2" />
                                            Reset MFA
                                        </DropdownMenuItem>
                                        {user.is_locked && (
                                            <DropdownMenuItem onClick={() => handleUnlock(user)} data-testid={`unlock-user-${user.id}`}>
                                                <LockOpen size={16} className="mr-2" />
                                                Sblocca account
                                            </DropdownMenuItem>
                                        )}
                                        <DropdownMenuItem onClick={() => handleRegenerateRecoveryCodes(user)} data-testid={`regen-codes-${user.id}`}>
                                            <Key size={16} className="mr-2" />
                                            Rigenera codici recupero
                                        </DropdownMenuItem>
                                        {user.id !== currentAuthUser?.id && (
                                            <DropdownMenuItem
                                                onClick={() => { setDeleting(user); setReassignTo(''); }}
                                                className="text-destructive focus:text-destructive"
                                                data-testid={`delete-user-${user.id}`}
                                            >
                                                <Trash2 size={16} className="mr-2" />
                                                Elimina Utente
                                            </DropdownMenuItem>
                                        )}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>

                            <div className="mt-4 space-y-2 text-sm">
                                <div className="flex items-center gap-2 text-muted-foreground">
                                    <Mail size={14} />
                                    <span className="truncate">{user.email}</span>
                                </div>
                                {user.phone && (
                                    <div className="flex items-center gap-2 text-muted-foreground">
                                        <Phone size={14} />
                                        <span>{user.phone}</span>
                                    </div>
                                )}
                                <div className="flex items-center gap-2 text-muted-foreground" data-testid={`last-login-${user.id}`}>
                                    <Clock size={14} />
                                    <span>Ultimo accesso: {formatLastLogin(user.last_login)}</span>
                                </div>
                            </div>

                            {user.countries.length > 0 && (
                                <div className="mt-4 pt-4 border-t">
                                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                                        Paesi assegnati
                                    </p>
                                    <div className="flex flex-wrap gap-1">
                                        {user.countries.map((country) => (
                                            <Badge key={country} variant="outline" className="text-xs">
                                                {country}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Create User Dialog */}
            <Dialog open={creating} onOpenChange={(v) => !v && setCreating(false)}>
                <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading uppercase">Nuovo Utente</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateUser} className="space-y-4 mt-4" data-testid="create-user-form">
                        <div>
                            <Label className="text-xs uppercase tracking-wider">Nome Completo *</Label>
                            <Input
                                value={createForm.name}
                                onChange={(e) => setCreateForm({...createForm, name: e.target.value})}
                                required
                                data-testid="new-user-name"
                            />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider">Email *</Label>
                            <Input
                                type="email"
                                value={createForm.email}
                                onChange={(e) => setCreateForm({...createForm, email: e.target.value})}
                                required
                                placeholder="nome@pezzaioli.it"
                                data-testid="new-user-email"
                            />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider">Telefono</Label>
                            <Input
                                value={createForm.phone}
                                onChange={(e) => setCreateForm({...createForm, phone: e.target.value})}
                                placeholder="+39 3XX XXXXXXX"
                                data-testid="new-user-phone"
                            />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider">Ruolo *</Label>
                            <Select
                                value={createForm.role}
                                onValueChange={(v) => setCreateForm({...createForm, role: v})}
                            >
                                <SelectTrigger data-testid="new-user-role">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="commerciale">Commerciale</SelectItem>
                                    <SelectItem value="area_manager">Area Manager</SelectItem>
                                    <SelectItem value="backoffice">Backoffice</SelectItem>
                                    <SelectItem value="addetto_consegna">Addetto alla Consegna</SelectItem>
                                    <SelectItem value="admin">Amministratore</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {createForm.role === 'commerciale' && areaManagers.length > 0 && (
                            <div>
                                <Label className="text-xs uppercase tracking-wider">Area Manager (team)</Label>
                                <Select
                                    value={createForm.manager_id || 'none'}
                                    onValueChange={(v) => setCreateForm({...createForm, manager_id: v})}
                                >
                                    <SelectTrigger data-testid="new-user-manager">
                                        <SelectValue placeholder="Nessuno" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Nessuno</SelectItem>
                                        {areaManagers.map((m) => (<SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>))}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground mt-1">Il commerciale sarà visibile all'area manager scelto.</p>
                            </div>
                        )}
                        <div>
                            <Label className="text-xs uppercase tracking-wider">Password Iniziale</Label>
                            <Input
                                value={createForm.password}
                                onChange={(e) => setCreateForm({...createForm, password: e.target.value})}
                                placeholder="Default: pezzaioli2024"
                                data-testid="new-user-password"
                            />
                            <p className="text-xs text-muted-foreground mt-1">Se lasci vuoto, verrà impostata a "pezzaioli2024"</p>
                        </div>
                        <div className="sticky bottom-0 -mx-6 -mb-6 px-6 py-4 bg-background border-t flex justify-end gap-3 z-10">
                            <Button type="button" variant="outline" onClick={() => setCreating(false)}>
                                Annulla
                            </Button>
                            <Button type="submit" className="btn-primary" data-testid="submit-new-user">
                                CREA UTENTE
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete User Dialog */}
            <Dialog open={!!deleting} onOpenChange={(v) => { if (!v) { setDeleting(null); setReassignTo(''); } }}>
                <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading uppercase text-destructive">
                            Elimina {deleting?.name}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4" data-testid="delete-user-dialog">
                        <div className="p-3 bg-destructive/10 text-destructive text-sm rounded-sm">
                            Stai per eliminare questo utente dal sistema. L'operazione non è reversibile.
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider">Riassegna clienti, opportunità e attività a</Label>
                            <Select value={reassignTo} onValueChange={setReassignTo}>
                                <SelectTrigger data-testid="reassign-select">
                                    <SelectValue placeholder="Seleziona un utente (facoltativo)" />
                                </SelectTrigger>
                                <SelectContent>
                                    {users
                                        .filter(u => u.id !== deleting?.id)
                                        .map(u => (
                                            <SelectItem key={u.id} value={u.id}>
                                                {u.name} {u.role === 'admin' ? '(Admin)' : ''}
                                            </SelectItem>
                                        ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground mt-1">
                                Obbligatorio se l'utente ha clienti/opportunità/attività in carico.
                            </p>
                        </div>
                        <div className="sticky bottom-0 -mx-6 -mb-6 px-6 py-4 bg-background border-t flex justify-end gap-3 z-10">
                            <Button variant="outline" onClick={() => { setDeleting(null); setReassignTo(''); }}>
                                Annulla
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={handleConfirmDelete}
                                data-testid="confirm-delete-user"
                            >
                                ELIMINA
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Assegna Area Manager Dialog */}
            <Dialog open={!!managingUser} onOpenChange={(v) => { if (!v) { setManagingUser(null); setSelectedManager(''); } }}>
                <DialogContent className="max-w-md" data-testid="assign-manager-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading uppercase">Assegna Area Manager</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-2">
                        <p className="text-sm text-muted-foreground">
                            Scegli l'Area Manager che potrà vedere e gestire i clienti, le commesse e i report di <strong>{managingUser?.name}</strong>.
                        </p>
                        {areaManagers.length === 0 ? (
                            <div className="p-3 rounded-sm bg-amber-50 border border-amber-200 text-sm text-amber-800" data-testid="no-area-managers-warning">
                                Non esiste ancora nessun Area Manager. Imposta prima un utente come "Area Manager" dal suo menu, poi torna qui per assegnarlo.
                            </div>
                        ) : (
                            <div>
                                <Label className="text-xs uppercase tracking-wider">Area Manager</Label>
                                <Select value={selectedManager} onValueChange={setSelectedManager}>
                                    <SelectTrigger data-testid="manager-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Nessuno (rimuovi dal team)</SelectItem>
                                        {areaManagers.map((m) => (<SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => { setManagingUser(null); setSelectedManager(''); }} data-testid="cancel-assign-manager">
                                Annulla
                            </Button>
                            <Button className="btn-primary font-heading" disabled={areaManagers.length === 0 || !selectedManager} onClick={handleSaveManager} data-testid="confirm-assign-manager">
                                SALVA
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Edit Profile Dialog */}
            <Dialog open={!!editingProfile} onOpenChange={() => setEditingProfile(null)}>
                <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading uppercase">
                            Modifica Profilo - {editingProfile?.name}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSaveProfile} className="space-y-4 mt-4" data-testid="edit-profile-form">
                        <div>
                            <Label className="text-xs uppercase tracking-wider">Nome Completo *</Label>
                            <Input
                                value={profileForm.name}
                                onChange={(e) => setProfileForm({...profileForm, name: e.target.value})}
                                required
                                data-testid="profile-name"
                            />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider">Email *</Label>
                            <Input
                                type="email"
                                value={profileForm.email}
                                onChange={(e) => setProfileForm({...profileForm, email: e.target.value})}
                                required
                                data-testid="profile-email"
                            />
                            <p className="text-xs text-muted-foreground mt-1">L'utente userà questa email per fare login</p>
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider">Telefono</Label>
                            <Input
                                value={profileForm.phone}
                                onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})}
                                placeholder="+39 3XX XXXXXXX"
                                data-testid="profile-phone"
                            />
                        </div>
                        <div className="sticky bottom-0 -mx-6 -mb-6 px-6 py-4 bg-background border-t flex justify-end gap-3 z-10">
                            <Button type="button" variant="outline" onClick={() => setEditingProfile(null)}>
                                Annulla
                            </Button>
                            <Button type="submit" className="btn-primary" data-testid="save-profile-btn">
                                SALVA
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Edit Countries Dialog */}
            <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
                <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading uppercase">
                            Modifica Paesi - {editingUser?.name}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div className="flex gap-2">
                            <Input
                                value={countriesInput}
                                onChange={(e) => setCountriesInput(e.target.value)}
                                placeholder="Aggiungi paese..."
                                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCountry())}
                                data-testid="country-input"
                            />
                            <Button onClick={handleAddCountry} variant="outline">
                                <Plus size={18} />
                            </Button>
                        </div>

                        <div className="flex flex-wrap gap-2 min-h-[80px] p-3 bg-muted/30 rounded-sm">
                            {editedCountries.length === 0 ? (
                                <p className="text-muted-foreground text-sm">Nessun paese assegnato</p>
                            ) : (
                                editedCountries.map((country) => (
                                    <Badge 
                                        key={country} 
                                        variant="secondary" 
                                        className="text-sm pr-1 flex items-center gap-1"
                                    >
                                        {country}
                                        <button
                                            onClick={() => handleRemoveCountry(country)}
                                            className="ml-1 hover:bg-muted rounded-full p-0.5"
                                        >
                                            <X size={14} />
                                        </button>
                                    </Badge>
                                ))
                            )}
                        </div>

                        <div className="flex justify-end gap-3 pt-4">
                            <Button variant="outline" onClick={() => setEditingUser(null)}>
                                Annulla
                            </Button>
                            <Button className="btn-primary" onClick={handleSaveCountries} data-testid="save-countries-btn">
                                SALVA
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={!!recoveryCodesDialog} onOpenChange={(v) => !v && setRecoveryCodesDialog(null)}>
                <DialogContent data-testid="recovery-codes-dialog">
                    <DialogHeader>
                        <DialogTitle>Codici di recupero — {recoveryCodesDialog?.name}</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">
                        Consegna questi <b>8 codici monouso</b> all'utente. Sostituiscono i precedenti e vengono mostrati <b>una sola volta</b>.
                    </p>
                    <div className="grid grid-cols-2 gap-2 p-3 bg-muted/30 rounded-sm border font-mono text-sm">
                        {(recoveryCodesDialog?.codes || []).map((rc, i) => (
                            <div key={i} className="tracking-wider text-center py-1">{rc}</div>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" className="flex-1"
                            onClick={() => navigator.clipboard?.writeText((recoveryCodesDialog?.codes || []).join('\n'))}
                            data-testid="copy-recovery-codes">Copia</Button>
                        <Button className="flex-1 btn-primary" onClick={() => setRecoveryCodesDialog(null)} data-testid="close-recovery-codes">Chiudi</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default UsersPage;
